import { AppBarEvents } from "@1js/office-start-appbar-events";
import AppBarIcons from "@1js/office-start-appbar-icons";
import {
  Button,
  Link,
  mergeClasses,
  Text,
  tokens,
} from "@fluentui/react-components";
import { FluentIconsProps, ImageOff32Regular } from "@fluentui/react-icons";
import { useFragment, useNovaEventing } from "@nova/react";
import React from "react";
import { AppBarIconProps } from "./Icons";
import { useSuggestedAppTileStyles } from "./SuggestedAppTile.styles";
import {
  dataFragment as SuggestedAppTile_m365App,
  SuggestedAppTileProps,
} from "./SuggestedAppTile.types";
const useInstallCallback = (id: string) => {
  const novaEventing = useNovaEventing();
  const installCallback = React.useCallback(
    async (reactEvent: React.SyntheticEvent) => {
      novaEventing.bubble({
        event: AppBarEvents.install(() => {
          return {
            tileID: id,
          };
        }),
        reactEvent,
      });
    },
    [id, novaEventing]
  );
  return { installCallback };
};
const useDescriptionCallback = (id: string) => {
  const novaEventing = useNovaEventing();
  const descriptionCallback = React.useCallback(
    async (reactEvent: React.SyntheticEvent) => {
      novaEventing.bubble({
        event: AppBarEvents.description(() => {
          return {
            tileID: id,
          };
        }),
        reactEvent,
      });
    },
    [id, novaEventing]
  );
  return { descriptionCallback };
};
const usePrivacyCallback = (id: string, privacyUrl: string) => {
  const novaEventing = useNovaEventing();
  const privacyCallback = React.useCallback(
    async (reactEvent: React.SyntheticEvent) => {
      novaEventing.bubble({
        event: AppBarEvents.privacy(() => {
          return {
            tileID: id,
            privacyUrl: privacyUrl,
          };
        }),
        reactEvent,
      });
    },
    [id, novaEventing, privacyUrl]
  );
  return { privacyCallback };
};
const useTermsOfUseCallback = (id: string, termsOfUseUrl: string) => {
  const novaEventing = useNovaEventing();
  const termsOfUseCallback = React.useCallback(
    async (reactEvent: React.SyntheticEvent) => {
      novaEventing.bubble({
        event: AppBarEvents.termsOfUse(() => {
          return {
            tileID: id,
            termsOfUseUrl: termsOfUseUrl,
          };
        }),
        reactEvent,
      });
    },
    [id, novaEventing, termsOfUseUrl]
  );
  return { termsOfUseCallback };
};
export const usePermissionsCallback = (id: string) => {
  const novaEventing = useNovaEventing();
  const permissionsCallback = React.useCallback(
    async (reactEvent: React.SyntheticEvent) => {
      novaEventing.bubble({
        event: AppBarEvents.permissions(() => {
          return {
            tileID: id,
          };
        }),
        reactEvent,
      });
    },
    [id, novaEventing]
  );
  return { permissionsCallback };
};
const isValidCallbackChangeEvent = (
  ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent
) => {
  if (!ev) {
    return false;
  }
  if ((ev as React.KeyboardEvent).code) {
    const code = (ev as React.KeyboardEvent).code;
    if (code !== "Enter" && code !== "Space") {
      return false;
    }
  }
  return true;
};
/**
 * Launch Tile React functional component.
 *
 */
export const SuggestedAppTile: React.FC<SuggestedAppTileProps> = ({
  m365App: m365AppRef,
  privacyLabel,
  termsOfUseLabel,
  permissionsLabel,
  addLabel,
  onClicked,
}) => {
  const {
    id,
    name,
    accessibleName,
    icon,
    shortDescription,
    privacyUrl,
    termsOfUseUrl,
    hasPermissions,
  } = useFragment(SuggestedAppTile_m365App, m365AppRef);
  const styles = useSuggestedAppTileStyles();
  const [iconError, setIconError] = React.useState(false);
  const { installCallback } = useInstallCallback(id);
  const { descriptionCallback } = useDescriptionCallback(id);
  const { privacyCallback } = usePrivacyCallback(id, privacyUrl ?? "");
  const { termsOfUseCallback } = useTermsOfUseCallback(id, termsOfUseUrl ?? "");
  const { permissionsCallback } = usePermissionsCallback(id);
  const onIconLoad = React.useCallback(() => {
    setIconError(false);
  }, []);
  const onIconError = React.useCallback(() => {
    setIconError(true);
  }, []);
  const useInstallButtonCallback = React.useCallback(
    (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent) => {
      if (ev) {
        // check to make sure we have a valid change event
        if (!isValidCallbackChangeEvent(ev)) {
          return;
        }
        ev.stopPropagation();
        installCallback(ev);
        if (onClicked) {
          onClicked();
        }
      }
    },
    [installCallback, onClicked]
  );
  const useSuggestedAppTileDescriptionCallback = React.useCallback(
    (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent) => {
      if (ev) {
        // check to make sure we have a valid change event
        if (!isValidCallbackChangeEvent(ev)) {
          return;
        }
        ev.stopPropagation();
        descriptionCallback(ev);
        if (onClicked) {
          onClicked();
        }
      }
    },
    [descriptionCallback, onClicked]
  );
  const usePrivacyLinkCallback = React.useCallback(
    (ev?: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent) => {
      if (ev) {
        // check to make sure we have a valid change event
        if (!isValidCallbackChangeEvent(ev)) {
          return;
        }
        ev.stopPropagation();
        privacyCallback(ev);
      }
      if (onClicked) {
        onClicked();
      }
    },
    [onClicked, privacyCallback]
  );
  const useTermsOfUseLinkCallback = React.useCallback(
    (ev?: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent) => {
      if (ev) {
        // check to make sure we have a valid change event
        if (!isValidCallbackChangeEvent(ev)) {
          return;
        }
        ev.stopPropagation();
        termsOfUseCallback(ev);
      }
      if (onClicked) {
        onClicked();
      }
    },
    [onClicked, termsOfUseCallback]
  );
  const usePermissionsLinkCallback = React.useCallback(
    (ev?: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent) => {
      if (ev) {
        // check to make sure we have a valid change event
        if (!isValidCallbackChangeEvent(ev)) {
          return;
        }
        ev.stopPropagation();
        permissionsCallback(ev);
        if (onClicked) {
          onClicked();
        }
      }
    },
    [onClicked, permissionsCallback]
  );
  const addButtonRef = React.useRef<
    HTMLAnchorElement | HTMLButtonElement | null
  >(null);
  const installCardRef = React.useRef<HTMLButtonElement | null>(null);
  const suggestedAppIconElement = React.useMemo(() => {
    if (!icon || iconError) {
      return <ImageOff32Regular primaryFill={tokens.colorNeutralForeground1} />;
    }
    if (icon && !("__typename" in icon)) {
      return icon;
    }
    switch (icon.__typename) {
      case "InternalIcon": {
        const IconComponent = AppBarIcons[icon.name] as React.FC<
          React.HTMLAttributes<SVGElement> & FluentIconsProps & AppBarIconProps
        >;
        return <IconComponent size="wide" className={styles.appIcon} />;
      }
      case "RemoteImage": {
        return (
          <>
            <img
              src={icon.src}
              alt={name}
              className={mergeClasses(styles.appIcon)}
              data-clicklog="custom"
              onLoad={onIconLoad}
              onError={onIconError}
            />
          </>
        );
      }
      default: {
        return undefined;
      }
    }
  }, [icon, iconError, styles.appIcon, name, onIconLoad, onIconError]);
  const suggestedAppLinkSection = React.useMemo(() => {
    return (
      <div className={styles.appContextLinksContainer}>
        {privacyUrl && (
          <span
            className={mergeClasses(
              styles.appContextLinkWrapper,
              styles.appContextLinkWrapperWithBorder
            )}
          >
            <Link
              as="a"
              className={styles.appContextLink}
              onClick={usePrivacyLinkCallback}
              onKeyDown={usePrivacyLinkCallback}
              title={privacyLabel}
            >
              <Text
                truncate
                wrap={false}
                className={styles.appContextLinkLabel}
                title={privacyLabel}
              >
                {privacyLabel}
              </Text>
            </Link>
          </span>
        )}
        {termsOfUseUrl && (
          <span
            className={mergeClasses(
              styles.appContextLinkWrapper,
              styles.appContextLinkWrapperWithBorder
            )}
          >
            <Link
              as="a"
              className={styles.appContextLink}
              onClick={useTermsOfUseLinkCallback}
              onKeyDown={useTermsOfUseLinkCallback}
            >
              <Text
                truncate
                wrap={false}
                className={styles.appContextLinkLabel}
                title={termsOfUseLabel}
              >
                {termsOfUseLabel}
              </Text>
            </Link>
          </span>
        )}
        {hasPermissions && (
          <span className={styles.appContextLinkWrapper}>
            <Link
              as="a"
              className={styles.appContextLink}
              onClick={usePermissionsLinkCallback}
              onKeyDown={usePermissionsLinkCallback}
            >
              <Text
                truncate
                wrap={false}
                className={styles.appContextLinkLabel}
                title={permissionsLabel}
              >
                {permissionsLabel}
              </Text>
            </Link>
          </span>
        )}
      </div>
    );
  }, [
    hasPermissions,
    permissionsLabel,
    privacyLabel,
    privacyUrl,
    styles.appContextLink,
    styles.appContextLinkLabel,
    styles.appContextLinkWrapper,
    styles.appContextLinkWrapperWithBorder,
    styles.appContextLinksContainer,
    termsOfUseLabel,
    termsOfUseUrl,
    usePermissionsLinkCallback,
    usePrivacyLinkCallback,
    useTermsOfUseLinkCallback,
  ]);
  return (
    <Button
      role="listitem"
      className={mergeClasses(
        styles.suggestedAppTile,
        styles.cardFocusIndicator
      )}
      id={`${id}-card`}
      ref={installCardRef}
      data-tid={`${id}-card`}
      aria-label={accessibleName || name}
      title={name}
      data-clicklog="custom"
      onClick={useSuggestedAppTileDescriptionCallback}
      onKeyDown={useSuggestedAppTileDescriptionCallback}
      size="small"
      tabIndex={0}
    >
      <div className={styles.suggestedAppTileContentWrapper}>
        <div className={styles.suggestedAppTileInfoContainer}>
          <div className={styles.appIconWrapper}>{suggestedAppIconElement}</div>
          <div className={styles.appContext}>
            <Text
              className={styles.appContextTitle}
              aria-label={accessibleName ?? name}
              truncate
              wrap={false}
            >
              {name}
            </Text>
            <Text
              title={shortDescription ?? ""}
              truncate
              wrap={false}
              className={styles.appContextDescription}
            >
              {shortDescription ?? ""}
            </Text>
            {suggestedAppLinkSection}
          </div>
        </div>
        <div className={styles.suggestedAppTileActionContainer}>
          <Button
            id={`${id}-add-button`}
            ref={addButtonRef}
            data-tid={`${id}-add-button`}
            aria-label={addLabel}
            data-clicklog="custom"
            className={styles.suggestedAppTileActionButton}
            onClick={useInstallButtonCallback}
            onKeyDown={useInstallButtonCallback}
            onAuxClick={undefined}
            title={addLabel}
          >
            <Text
              truncate
              wrap={false}
              className={styles.suggestedAppTileActionButtonLabel}
            >
              {addLabel}
            </Text>
          </Button>
        </div>
      </div>
    </Button>
  );
};
export default SuggestedAppTile;
